import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  SkipForward,
  SkipBack,
  RotateCw,
  Volume2,
  VolumeX,
  Wifi,
  WifiOff,
  Radio,
} from "lucide-react";

const formatCountdown = (ms) => {
  if (ms <= 0) return "expired";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem.toString().padStart(2, "0")}`;
};

export default function RemoteControl() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [sending, setSending] = useState(null);
  const [lastAction, setLastAction] = useState(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/signage/pair/${code}`);
        setInfo(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Pair code invalid");
      }
    })();
  }, [code]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const expiresMs = useMemo(() => {
    if (!info?.expires_at) return 0;
    return new Date(info.expires_at).getTime() - now;
  }, [info, now]);

  const isExpired = expiresMs <= 0;

  const send = async (action) => {
    if (isExpired) {
      toast.error("Pair code expired — ask the manager for a fresh one");
      return;
    }
    setSending(action);
    try {
      await api.post(`/signage/pair/${code}/command`, { action });
      setLastAction({ action, at: Date.now() });
      if (action === "mute") setMuted(true);
      if (action === "unmute") setMuted(false);
      // Light haptic-style toast
      toast.success(ACTION_LABEL[action]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Command failed");
    } finally {
      setSending(null);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#0A0908] text-white flex items-center justify-center p-8">
        <div className="max-w-sm text-center">
          <div className="inline-flex p-4 rounded-2xl bg-red-500/10 text-red-400 mb-5">
            <WifiOff size={28} />
          </div>
          <div className="text-3xl font-black tracking-tighter mb-2">
            Pair code invalid
          </div>
          <div className="text-zinc-400 text-sm">
            {err}. Ask the manager to open the Signage dashboard and tap
            <span className="text-[#E8D6A3] font-semibold"> Share QR → Pair</span>
            &nbsp;to generate a fresh code.
          </div>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-[#0A0908] text-zinc-500 flex items-center justify-center">
        Loading…
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#0A0908] text-white flex flex-col"
      data-testid="remote-control"
    >
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#E8D6A3]">
          <Radio size={12} /> Mirrorloops · screen remote
        </div>
        <div className="text-3xl font-black tracking-tighter mt-1">
          {info.screen_name}
        </div>
        <div className="flex items-center gap-2 mt-2 text-xs">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
              isExpired
                ? "border-red-500/40 text-red-400"
                : "border-emerald-500/40 text-emerald-400"
            }`}
            data-testid="remote-status-pill"
          >
            {isExpired ? <WifiOff size={10} /> : <Wifi size={10} />}
            {isExpired ? "expired" : "connected"}
          </span>
          <span className="text-zinc-500">
            session ends in{" "}
            <span className="text-white font-semibold" data-testid="remote-countdown">
              {formatCountdown(expiresMs)}
            </span>
          </span>
        </div>
      </div>

      <div className="flex-1 p-6 flex flex-col items-center justify-center gap-5">
        <div className="text-center mb-2">
          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-2">
            Quick controls
          </div>
          <div className="text-zinc-400 text-sm">
            Tap any control — the screen updates within 3 seconds.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
          <BigButton
            onClick={() => send("prev")}
            disabled={sending === "prev" || isExpired}
            icon={<SkipBack size={24} />}
            label="Previous"
            testid="remote-btn-prev"
          />
          <BigButton
            onClick={() => send("next")}
            disabled={sending === "next" || isExpired}
            icon={<SkipForward size={24} />}
            label="Next"
            primary
            testid="remote-btn-next"
          />
          <BigButton
            onClick={() => send(muted ? "unmute" : "mute")}
            disabled={sending === "mute" || sending === "unmute" || isExpired}
            icon={muted ? <VolumeX size={24} /> : <Volume2 size={24} />}
            label={muted ? "Unmute" : "Mute"}
            testid="remote-btn-mute"
          />
          <BigButton
            onClick={() => send("reload")}
            disabled={sending === "reload" || isExpired}
            icon={<RotateCw size={24} />}
            label="Reload"
            testid="remote-btn-reload"
          />
        </div>

        {lastAction && (
          <div
            className="mt-4 text-xs text-zinc-500"
            data-testid="remote-last-action"
          >
            Last sent: <span className="text-[#E8D6A3] font-semibold">{ACTION_LABEL[lastAction.action]}</span>
          </div>
        )}
      </div>

      <div className="p-6 border-t border-white/5 text-[11px] text-zinc-600 text-center">
        Staff-only remote session · commands go directly to the player tablet.
        Scan a fresh QR after {PAIR_TTL_MINUTES_LABEL}.
      </div>
    </div>
  );
}

const ACTION_LABEL = {
  next: "Next clip",
  prev: "Previous clip",
  reload: "Reloading screen",
  mute: "Muted",
  unmute: "Unmuted",
};

const PAIR_TTL_MINUTES_LABEL = "30 minutes";

function BigButton({ onClick, disabled, icon, label, primary, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`flex flex-col items-center justify-center gap-2 py-8 rounded-2xl border transition active:scale-95 ${
        primary
          ? "bg-[#D4B878] hover:bg-[#E8D6A3] text-black border-[#D4B878] font-bold"
          : "bg-white/[0.03] hover:bg-white/[0.06] border-white/10 text-white"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {icon}
      <span className="text-sm">{label}</span>
    </button>
  );
}
